import { ExclamationIcon } from '@heroicons/react/solid'
import { useWeb3React } from '@web3-react/core'
import { ArcTable, Box, IHeader, ITableRow, Text } from 'components'
import { configConstants } from 'config'
import { useCreditContext, useModal } from 'contexts'
import { BigNumber, utils } from 'ethers'
import { useAsync, useCapsulesLoading } from 'hooks'
import { useRef } from 'react'
import { formatBigNumber, getIconUrlFromGCP, getSpacing } from 'utils'
import { VaultModal } from '../VaultModal'

export const VaultTableDesktop = () => {
  const { chainId, account } = useWeb3React()
  const { coreCapsules, creditScore, poolCapsules, isGlobalRefreshing } = useCreditContext()
  const { openWith } = useModal()
  const fomatter = useRef(
    Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits: 1 }),
  )

  const isOnUnsupportedChain = chainId && ![1, 137, 80001, 5].includes(chainId)
  const isLoading = useCapsulesLoading(Array.from(coreCapsules.values())) || isGlobalRefreshing

  const [rows] = useAsync<ITableRow[]>(async () => {
    const rows: ITableRow[] = []

    if (coreCapsules.size === 0 || poolCapsules.size === 0 || !creditScore || isLoading) {
      if (isOnUnsupportedChain || !account) {
        rows.push({
          cells: [
            <div className="mb-5" key={0}>
              <div className="absolute left-1/2 transform -translate-x-1/2  whitespace-nowrap">
                <Text weight="regular" size="s">
                  {isOnUnsupportedChain
                    ? 'Unsupported network. Please switch to a supported chain to start borrowing.'
                    : 'Please connect your wallet to start borrowing.'}
                </Text>
              </div>
            </div>,
          ],
        })
        return rows
      }

      return
    }

    let keyCount = 0
    const sortedCoreCapsules = Array.from(coreCapsules.values()).sort(
      (a, b) =>
        parseInt(a.creditLimitAttributes.threshold || '0') -
        parseInt(b.creditLimitAttributes.threshold || '0'),
    )

    for (const coreCapsule of sortedCoreCapsules) {
      const { threshold, title: coreTitle } = coreCapsule.creditLimitAttributes
      const { maxLTV, totalValueLocked: tvl, yearlyInterestRate, accountProperties } = coreCapsule

      const maxLtvRounded = Number(formatBigNumber(maxLTV.mul(100), 2)).toFixed(0)
      const borrowUsage = coreCapsule.accountProperties?.borrowUsage || BigNumber.from(0)

      const isLocked = !coreCapsule.accountCanView
      const coreSymbol = coreCapsule.collateralSymbol

      const onRowClicked = () => {
        openWith(<VaultModal coreCapsule={coreCapsule} />, {
          hideCloseButton: true,
        })
      }

      rows.push({
        onClick: isLocked ? undefined : onRowClicked,
        cells: [
          <CoreAssetName
            coreName={coreSymbol}
            vaultName={coreTitle}
            key={keyCount++}
            isLocked={isLocked}
          />,
          `${threshold || '0'}+`,
          `Up to ${maxLtvRounded}% LTV`,
          `$${fomatter.current.format(parseFloat(utils.formatEther(tvl)))}`,
          `${formatBigNumber(yearlyInterestRate.mul(100))}%`,
          isLocked ? (
            '-'
          ) : (
            <BorrowUsage
              borrowUsage={borrowUsage}
              accountCollateral={accountProperties.collateralAmountScaled}
              key={keyCount++}
            />
          ),
        ],
      })
    }

    return rows
  }, [coreCapsules, poolCapsules, isLoading, account])

  const headers: IHeader[] = [
    {
      name: 'Vault',
    },
    {
      name: 'Score threshold',
      tooltip: 'The minimum credit score required to access the vault',
      alignContent: 'right',
    },
    {
      name: 'Capital efficiency',
      tooltip: 'The maximum LTV ratio on this vault given a 999 credit score',
      alignContent: 'right',
    },
    {
      name: 'Total value locked',
      tooltip: 'The $USD value of collateral locked in the vault',
      alignContent: 'right',
    },
    {
      name: 'Interest rate',
      tooltip: 'The current interest rate charged for borrowing on the vault',
      alignContent: 'right',
    },
    {
      name: 'Borrow usage',
      tooltip:
        'Represents borrow amount relative to available credit and determines how fast your credit score grows',
      alignContent: 'right',
    },
  ]

  return <ArcTable headers={headers} rows={rows} loading={isLoading} hidePagination={true} />
}

interface ICoreAssetNameProps {
  coreName: string
  vaultName: string
  isLocked: boolean
}

const CoreAssetName = ({ coreName, vaultName, isLocked }: ICoreAssetNameProps) => {
  return (
    <Box flexDirection="row" alignItems="center">
      <img src={getIconUrlFromGCP(isLocked ? 'locked' : coreName.toLowerCase())} width="26px" />

      <Box marginLeft={getSpacing('s')}>
        <Text weight="regular" size="xs" color="WHITE" style={{ opacity: isLocked ? 0.5 : 1 }}>
          {vaultName}
        </Text>
      </Box>
    </Box>
  )
}

interface IBorrowUsageProps {
  borrowUsage: BigNumber
  accountCollateral: BigNumber
}

const BorrowUsage = ({ borrowUsage, accountCollateral }: IBorrowUsageProps) => {
  return (
    <Box flexDirection="row" alignItems="center" justifyContent={'right'}>
      {borrowUsage.gt(configConstants.atRiskThreshold) && (
        <ExclamationIcon width={'24px'} fill={'ORANGE'} />
      )}
      {accountCollateral.eq(0) ? (
        <Text size="xs" weight="regular">
          -
        </Text>
      ) : (
        <Text
          size="xs"
          weight="regular"
          color={borrowUsage.lt(configConstants.atRiskThreshold) ? 'GREEN_5' : 'RED_5'}
        >{`${formatBigNumber(borrowUsage.mul(100), 2)}%`}</Text>
      )}
    </Box>
  )
}
